import { state, getSelectedKeyframe, pushUndo, notify, snapshotKf, recordKfHistory } from './state.js';
import { addRecent } from './unicode.js';
import { renderKeyframeStrip } from './keyframes.js';
import { renderTimeline } from './timeline.js';

// ================================================================
// CHARACTER GRID
// ================================================================
export function renderGrid() {
  const container = document.getElementById("char-grid");
  container.innerHTML = "";
  const kf = getSelectedKeyframe();
  if (!kf) {
    container.textContent = "No keyframe selected";
    return;
  }

  container.style.gridTemplateColumns = `repeat(${kf.cols}, 1ch)`;
  container.style.gridTemplateRows = `repeat(${kf.rows}, 1em)`;
  container.style.fontSize = state.project.fontSize + "px";

  const wrapper = container.parentElement;
  if (wrapper) {
    wrapper.style.backgroundColor = kf.backgroundColor || state.project.backgroundColor || "";
  }

  for (let r = 0; r < kf.rows; r++) {
    for (let c = 0; c < kf.cols; c++) {
      const cell = kf.grid[r][c];
      const div = document.createElement("div");
      div.className = "char-cell";
      if (state.ui.selectedCell && state.ui.selectedCell.row === r && state.ui.selectedCell.col === c) {
        div.classList.add("selected");
      }
      if (cell.color) {
        div.classList.add("has-color");
        div.style.color = cell.color;
      } else {
        div.style.color = kf.defaultColor;
      }
      div.textContent = cell.char || " ";
      div.addEventListener("click", () => onCellClick(r, c));
      container.appendChild(div);
    }
  }
}

function onCellClick(row, col) {
  const kf = getSelectedKeyframe();
  if (!kf) return;
  state.ui.selectedCell = { row, col };

  if (state.ui.selectedChar !== null) {
    pushUndo();
    const snap = snapshotKf(kf);
    kf.grid[row][col].char = state.ui.selectedChar;
    addRecent(state.ui.selectedChar);
    notify("paintCell");
    recordKfHistory(kf, "paint stroke", snap);
  }

  renderGrid();
  renderGridProps();
}

// ================================================================
// GRID CONTROLS (resize)
// ================================================================
export function renderGridControls() {
  const container = document.getElementById("grid-controls");
  container.innerHTML = "";
  const kf = getSelectedKeyframe();
  if (!kf) return;

  const info = document.createElement("span");
  info.textContent = `${kf.cols}x${kf.rows}`;
  container.appendChild(info);

  const btns = [
    { label: "+Col", fn: () => resizeGrid(0, 1) },
    { label: "-Col", fn: () => resizeGrid(0, -1) },
    { label: "+Row", fn: () => resizeGrid(1, 0) },
    { label: "-Row", fn: () => resizeGrid(-1, 0) },
  ];

  for (const b of btns) {
    const btn = document.createElement("button");
    btn.textContent = b.label;
    btn.addEventListener("click", b.fn);
    container.appendChild(btn);
  }
}

function resizeGrid(dRows, dCols) {
  const kf = getSelectedKeyframe();
  if (!kf) return;
  const newRows = Math.max(1, kf.rows + dRows);
  const newCols = Math.max(1, kf.cols + dCols);
  if (newRows === kf.rows && newCols === kf.cols) return;

  pushUndo();
  const snap = snapshotKf(kf);
  while (kf.grid.length < newRows) {
    kf.grid.push(Array.from({length: kf.cols}, () => ({char: "", color: null})));
  }
  while (kf.grid.length > newRows) kf.grid.pop();

  for (let r = 0; r < newRows; r++) {
    while (kf.grid[r].length < newCols) kf.grid[r].push({char: "", color: null});
    while (kf.grid[r].length > newCols) kf.grid[r].pop();
  }
  kf.rows = newRows;
  kf.cols = newCols;

  state.project.gridSize = { rows: newRows, cols: newCols };

  if (state.ui.selectedCell) {
    if (state.ui.selectedCell.row >= newRows || state.ui.selectedCell.col >= newCols) {
      state.ui.selectedCell = null;
    }
  }
  notify("resize");
  recordKfHistory(kf, "resize", snap);
  renderGrid();
  renderGridControls();
  renderGridProps();
}

// ================================================================
// GRID PROPERTIES
// ================================================================
export function renderGridProps() {
  const container = document.getElementById("grid-props");
  container.innerHTML = "";
  const kf = getSelectedKeyframe();
  if (!kf) return;

  // Name
  const nameLabel = document.createElement("label");
  nameLabel.textContent = "Name: ";
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.value = kf.name;
  nameInput.addEventListener("change", () => {
    pushUndo();
    const snap = snapshotKf(kf);
    kf.name = nameInput.value;
    notify("rename");
    recordKfHistory(kf, "rename", snap);
    renderKeyframeStrip();
    renderTimeline();
  });
  nameLabel.appendChild(nameInput);
  container.appendChild(nameLabel);

  // Default color
  const colorLabel = document.createElement("label");
  colorLabel.textContent = "Color: ";
  const colorInput = document.createElement("input");
  colorInput.type = "color";
  colorInput.value = kf.defaultColor;
  colorInput.addEventListener("input", () => {
    pushUndo();
    const snap = snapshotKf(kf);
    kf.defaultColor = colorInput.value;
    notify("color");
    recordKfHistory(kf, "default color", snap);
    renderGrid();
  });
  colorLabel.appendChild(colorInput);
  container.appendChild(colorLabel);

  // Cell color (if cell selected)
  if (state.ui.selectedCell) {
    const cell = kf.grid[state.ui.selectedCell.row][state.ui.selectedCell.col];
    const cellColorLabel = document.createElement("label");
    cellColorLabel.textContent = "Cell: ";
    const cellColorInput = document.createElement("input");
    cellColorInput.type = "color";
    cellColorInput.value = cell.color || kf.defaultColor;
    cellColorInput.addEventListener("input", () => {
      pushUndo();
      const snap = snapshotKf(kf);
      cell.color = cellColorInput.value;
      notify("cellColor");
      recordKfHistory(kf, "cell color", snap);
      renderGrid();
    });
    cellColorLabel.appendChild(cellColorInput);

    const clearBtn = document.createElement("button");
    clearBtn.className = "btn-sm";
    clearBtn.textContent = "Reset";
    clearBtn.addEventListener("click", () => {
      pushUndo();
      const snap = snapshotKf(kf);
      cell.color = null;
      notify("cellColor");
      recordKfHistory(kf, "cell color reset", snap);
      renderGrid();
      renderGridProps();
    });

    container.appendChild(cellColorLabel);
    container.appendChild(clearBtn);

    // Show cell info
    const info = document.createElement("span");
    const ch = cell.char || " ";
    info.textContent = `[${state.ui.selectedCell.row},${state.ui.selectedCell.col}] "${ch}" U+${ch.codePointAt(0)?.toString(16).toUpperCase().padStart(4, "0") || "0020"}`;
    info.style.fontSize = "10px";
    info.style.color = "#557";
    container.appendChild(info);
  }

  // Font size
  const fontLabel = document.createElement("label");
  fontLabel.textContent = "Size: ";
  const fontInput = document.createElement("input");
  fontInput.type = "number";
  fontInput.value = state.project.fontSize;
  fontInput.min = 8;
  fontInput.max = 200;
  fontInput.addEventListener("change", () => {
    state.project.fontSize = parseInt(fontInput.value) || 48;
    notify("fontSize");
    renderGrid();
  });
  fontLabel.appendChild(fontInput);
  container.appendChild(fontLabel);
}
